// Runtime DOM translation engine. When English is active, it walks the DOM and
// replaces any text node / placeholder / title / aria-label whose trimmed text
// exactly matches a dictionary entry. Dynamic Hebrew content (not in the dict)
// is left untouched. Works across ALL pages without editing each component.
import { DICT, REGEX_RULES, DATE_TOKENS } from './dictionary';

let observer = null;
let active = false;
let pending = false;

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT']);
const ATTRS = ['placeholder', 'title', 'aria-label'];
const HAS_DIGIT = /\d/;
const HAS_HEBREW = /[\u0590-\u05FF]/;

// Normalize Hebrew punctuation variants (geresh/gershayim ↔ ASCII quotes)
const norm = (s) => s.replace(/[\u05f3\u2018\u2019]/g, "'").replace(/[\u05f4\u201c\u201d]/g, '"');

let normIndex = null;
let normIndexSize = -1;
function lookupDict(key) {
  const direct = DICT[key];
  if (direct != null) return direct;
  const keys = Object.keys(DICT);
  if (!normIndex || keys.length !== normIndexSize) {
    normIndex = {};
    for (const k of keys) normIndex[norm(k)] = DICT[k];
    normIndexSize = keys.length;
  }
  return normIndex[norm(key)];
}

function translateValue(value) {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || !HAS_HEBREW.test(trimmed)) return null;
  const en = lookupDict(trimmed);
  if (en != null) return value.replace(trimmed, () => en);
  for (const [re, sub] of REGEX_RULES) {
    if (re.test(trimmed)) {
      const out = trimmed.replace(re, sub);
      return value.replace(trimmed, () => out);
    }
  }
  // Composite strings containing dates: translate weekday/month tokens
  if (HAS_DIGIT.test(trimmed)) {
    let out = norm(trimmed);
    for (const [he, en2] of DATE_TOKENS) out = out.split(norm(he)).join(en2);
    if (out !== norm(trimmed)) return value.replace(trimmed, () => out);
  }
  return null;
}

function translateTextNode(node) {
  const p = node.parentNode;
  if (!p || SKIP_TAGS.has(p.nodeName)) return;
  const nv = translateValue(node.nodeValue);
  if (nv != null && nv !== node.nodeValue) node.nodeValue = nv;
}

function translateAttrs(el) {
  if (!el.getAttribute) return;
  for (const attr of ATTRS) {
    if (el.hasAttribute(attr)) {
      const v = el.getAttribute(attr);
      const nv = translateValue(v);
      if (nv != null && nv !== v) el.setAttribute(attr, nv);
    }
  }
}

function walk(root) {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) { translateTextNode(root); return; }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(translateTextNode);
  if (root.querySelectorAll) {
    root.querySelectorAll('[placeholder],[title],[aria-label]').forEach(translateAttrs);
    if (root.matches && root.matches('[placeholder],[title],[aria-label]')) translateAttrs(root);
  }
}

function startObserve() {
  if (observer || !document.body) return;
  observer = new MutationObserver((mutations) => {
    if (!active) return;
    observer.disconnect();
    for (const m of mutations) {
      if (m.type === 'characterData') translateTextNode(m.target);
      else if (m.type === 'attributes') translateAttrs(m.target);
      else if (m.type === 'childList') {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) translateTextNode(n);
          else if (n.nodeType === Node.ELEMENT_NODE) walk(n);
        });
      }
    }
    if (active) observer.observe(document.body, OBS_CFG);
  });
  observer.observe(document.body, OBS_CFG);
}

const OBS_CFG = {
  subtree: true,
  childList: true,
  characterData: true,
  attributes: true,
  attributeFilter: ATTRS,
};

export function startTranslation() {
  if (active) return;
  active = true;
  const run = () => {
    walk(document.body);
    startObserve();
  };
  if (document.body) run();
  else window.addEventListener('DOMContentLoaded', run, { once: true });
  // Extra passes to catch async-rendered content
  [150, 600, 1500].forEach((t) => setTimeout(() => active && scheduleFull(), t));
}

export function scheduleFull() {
  if (pending || !active) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    if (!active) return;
    if (observer) observer.disconnect();
    walk(document.body);
    if (active) {
      if (observer && document.body) observer.observe(document.body, OBS_CFG);
      else startObserve();
    }
  });
}

export function stopTranslation() {
  active = false;
  if (observer) { observer.disconnect(); observer = null; }
}
